import { getScanPhotoImageUrl } from '@kaiizen/kai-pro-api';
import { getLatestScanPhotos, ScanGroupOptimized, ScanPhoto, ScanRootOptimized, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

import { ScopeReportLineItem, TaskPhoto, TaskPhotosMap, TaskPhotoWithName } from './ScopeReportTypes';

export const extractPhotosFromLineItems = (items: ScopeReportLineItem[]): TaskPhotoWithName[] => {
  const taskPhotoMap = new Map<string, TaskPhotoWithName>();

  items.forEach(item => {
    if (item.taskPhotos?.length && item.taskId) {
      const compositeKey = `${item.groupId}|${item.taskId}`;
      if (!taskPhotoMap.has(compositeKey)) {
        taskPhotoMap.set(compositeKey, {
          taskName: item.taskName,
          taskPhotos: item.taskPhotos,
        });
      }
    }
  });

  return Array.from(taskPhotoMap.values());
};

// export const getTaskPhotoCompositeKey = (task: ScanTaskOptimized, group: ScanGroupOptimized) => `${task.id}|${getGroupName(group)}`;

const scanPhotoToTaskPhoto = (scanPhoto: ScanPhoto): TaskPhoto => {
  return {
    id: scanPhoto.photoId,
    dataUrl: getScanPhotoImageUrl(scanPhoto.photoId, { maxDimension: 1024 }),
  };
};

export const getTaskPhotosMap = (scanRoot: ScanRootOptimized): TaskPhotosMap => {
  const taskPhotosMap = new Map<string, TaskPhoto[]>();
  if (!scanRoot.groups) return taskPhotosMap;
  for (const group of scanRoot.groups) {
    if (!group.tasks) continue;
    for (const task of group.tasks) {
      const taskPhotos = getTaskPhotos(task);
      if (taskPhotos.length > 0) taskPhotosMap.set(task.id, taskPhotos);
    }
  }
  return taskPhotosMap;
};

const SCOPE_REPORT_MAX_PHOTOS_PER_TASK = 3;

export const getTaskPhotos = (task: ScanTaskOptimized): TaskPhoto[] => {
  const latestPhotos = getLatestScanPhotos(task.optimized?.allPhotos, SCOPE_REPORT_MAX_PHOTOS_PER_TASK);
  return latestPhotos.map(scanPhotoToTaskPhoto);
};

/**
 * Represents photos at either group level or task level within a group.
 * When task is null/undefined, photos are group-level and go in the group directory.
 * When task is set, photos are task-level and go in the task directory inside the group.
 */
export interface TaskWithPhotos {
  group: ScanGroupOptimized;
  task: ScanTaskOptimized | null;
  photos: ScanPhoto[];
}

/**
 * Generator that yields group-level and task-level photos, organized by group structure.
 * For each group, yields group-level photos first (if any), then each task's photos.
 * Group-level photos are written to the group directory; task-level to group/task.
 *
 * @param scanRoot The optimized scan root data
 * @yields Group or task with their photos and group context
 */
export function* iterateTasksWithPhotosByGroup(scanRoot: ScanRootOptimized): Generator<TaskWithPhotos> {
  if (!scanRoot.groups) return;

  for (const group of scanRoot.groups) {
    const groupPhotos = group.optimized?.allPhotos || [];
    if (groupPhotos.length > 0) {
      yield { group, task: null, photos: groupPhotos };
    }

    if (!group.tasks) continue;

    for (const task of group.tasks) {
      const photos = task.optimized?.allPhotos || [];
      if (photos.length > 0) {
        yield { group, task, photos };
      }
    }
  }
}
